/**
 * 회원 탈퇴 확인 페이지
 * 
 * @description
 * 사용자가 회원 탈퇴를 최종 확인하고 실행하는 페이지입니다.
 * 
 * @features
 * - 회원 탈퇴 최종 확인 메시지 표시
 * - 회원 탈퇴 실행
 * - 탈퇴 완료 후 완료 페이지로 이동
 * - AlertDialog 통합 (성공/실패 메시지)
 * 
 * @important
 * 실제 계정 삭제가 진행됩니다.
 * 
 * @api
 * - DELETE /user/my-account
 * 
 * @routes
 * - /account-deletion/confirm (현재 페이지)
 * - /account-deletion/complete (탈퇴 완료 후)
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { Button, AlertDialog } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { useUser } from '@/data/queries/useUser';
import { useAuth } from '@/contexts/AuthContext';
import { removeLinkedAccountSession } from '@/utils/linkedAccountSessions';

const LINKED_ACCOUNTS_KEY = 'medicrew_linked_accounts';

function removeDeletedAccountFromLinkedAccounts(accountId?: string, loginId?: string): void {
	if (!accountId && !loginId) return;
	try {
		const raw = localStorage.getItem(LINKED_ACCOUNTS_KEY);
		if (!raw) return;
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return;
		const remainingAccounts = parsed.filter((account: unknown) => {
			if (!account || typeof account !== 'object') return true;
			const entry = account as { accountId?: unknown; loginId?: unknown };
			return entry.accountId !== accountId && entry.loginId !== loginId;
		});
		if (remainingAccounts.length > 0) {
			localStorage.setItem(LINKED_ACCOUNTS_KEY, JSON.stringify(remainingAccounts));
		} else {
			localStorage.removeItem(LINKED_ACCOUNTS_KEY);
		}
	} catch {
		// 손상된 저장값은 재노출 방지를 위해 제거합니다.
		localStorage.removeItem(LINKED_ACCOUNTS_KEY);
	}
}

export default function AccountDeletionConfirm(): React.ReactElement {
	const { deleteAccount } = useUser();
	const location = useLocation();
	const { t } = useTranslation('auth');
	const reasonFromState = (location.state as { reason?: string } | null)?.reason ?? '';

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [feedback, setFeedback] = useState<string>(reasonFromState);
	const [alertState, setAlertState] = useState<{ isOpen: boolean; message: string; }>({
		isOpen: false,
		message: '',
	});

	// ========== Hooks ==========
	
	const navigate = useNavigate();
	const { logout, user } = useAuth();

	// ========== Helper 함수 ==========
	
	/**
	 * AlertDialog 표시
	 * @param message - 표시할 메시지
	 * @description AlertDialog를 열고 메시지를 설정
	 */
	const showAlert = (message: string): void => {
		setAlertState({
			isOpen: true,
			message,
		});
	};

	/**
	 * AlertDialog 닫기
	 * @description AlertDialog를 닫음
	 */
	const closeAlert = (): void => {
		setAlertState({
			isOpen: false,
			message: '',
		});
	};

	// ========== Event Handlers ==========
	
	/**
	 * 삭제하기 버튼 핸들러
	 * @description 회원 탈퇴를 실행하는 함수
	 * 
	 * @process
	 * 1. 회원 탈퇴 API 호출
	 * 2. 성공 시 AlertDialog 표시
	 * 3. 로그아웃 처리 (AuthContext 상태 초기화 + localStorage 정리)
	 * 4. 완료 페이지로 이동
	 */
	const handleDelete = async (): Promise<void> => {
		try {
			setIsLoading(true);
			const deletedAccountId = user?.currentAccount?.accountId;
			const deletedLoginId = user?.currentAccount?.loginId;
			await deleteAccount(feedback.trim() || undefined);
			removeDeletedAccountFromLinkedAccounts(deletedAccountId, deletedLoginId);
			if (deletedAccountId) {
				removeLinkedAccountSession(deletedAccountId);
			}
		
		// 성공 메시지 표시
		showAlert(t('accountDeletion.confirm.alertSuccess'));
		
		// 로그아웃 처리 (AuthContext 상태 초기화 + localStorage 정리)
			logout();
			
			// 1.5초 후 완료 페이지로 이동
			setTimeout(() => {
				navigate(ROUTES.AUTH.ACCOUNT_DELETION_COMPLETE);
			}, 1500);
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : t('accountDeletion.confirm.alertError');
			showAlert(message || t('accountDeletion.confirm.alertError'));
		} finally {
			// 항상 실행: 로딩 상태 종료
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex flex-col items-center justify-center bg-[#F8F9FA] p-8">
			<div className="w-full max-w-[520px] bg-white rounded-3xl p-14 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] border border-gray-100 flex flex-col items-center text-center">

				{/* Icon */}
				<div className="mb-8 relative">
					<div className="absolute -top-1 -right-1">
						<Star size={32} className="text-blue-500 fill-blue-500" strokeWidth={1.5} />
					</div>
					<div className="rotate-[-15deg] translate-x-[-10px] translate-y-[10px]">
						<div className="flex flex-col gap-1 items-end">
							<div className="w-6 h-0.5 bg-blue-400 rounded-full opacity-40"></div>
							<div className="w-8 h-0.5 bg-blue-500 rounded-full opacity-70"></div>
							<div className="w-10 h-0.5 bg-blue-600 rounded-full"></div>
						</div>
					</div>
				</div>

				<h1 className="text-2xl font-semibold text-brand-500 mb-12">
					{t('accountDeletion.confirm.pageTitle')}
				</h1>

				<div className="text-[#374151] text-lg leading-relaxed mb-8">
					<p className="mb-1">{t('accountDeletion.confirm.warningLine1')}</p>
					<p className="mb-6">{t('accountDeletion.confirm.warningLine2')}</p>
					<p className="font-bold text-gray-900">{t('accountDeletion.confirm.question')}</p>
				</div>

				{/* 피드백 입력 */}
				<div className="w-full mb-8 text-left">
					<label className="block text-sm font-medium text-gray-600 mb-2">
						{t('accountDeletion.confirm.reasonLabel')}
					</label>
					<textarea
						value={feedback}
						onChange={(e) => setFeedback(e.target.value)}
						placeholder={t('accountDeletion.confirm.reasonPlaceholder')}
						rows={3}
						maxLength={500}
						disabled={isLoading}
						className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-700 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
					/>
					<p className="text-xs text-gray-400 text-right mt-1">{feedback.length}/500</p>
				</div>

				{/* Actions */}
				<div className="flex gap-4 w-full max-w-[360px]">
					<Button
						onClick={handleDelete}
						className="flex-1 rounded-full text-lg shadow-lg shadow-blue-500/20 h-11 bg-[#0085FF] hover:bg-[#0077E6]"
						variant="primary"
						disabled={isLoading}
					>
						{isLoading ? t('accountDeletion.confirm.deletingButton') : t('accountDeletion.confirm.deleteButton')}
					</Button>
					<Button
						onClick={() => navigate(-1)}
						className="flex-1 rounded-full text-lg h-11 text-[#0085FF] border-2 border-[#0085FF] hover:bg-gray-50"
						variant="secondary"
						disabled={isLoading}
					>
						{t('cancel')}
					</Button>
				</div>
			</div>

			{/* Footer */}
			<div className="mt-12 flex items-center gap-2">
				<span className="text-xs text-gray-400">Powered by</span>
				<span className="text-sm font-bold text-gray-800 tracking-tight">NEWBASE</span>
			</div>

			{/* AlertDialog */}
			<AlertDialog
				isOpen={alertState.isOpen}
				onClose={closeAlert}
				message={alertState.message}
			/>
		</div>
	);
}
